import {
  AbsenceIcon,
  ClassbookIcon,
  CoursesIcon,
  DashboardIcon,
  FileIcon,
  GroupsIcon,
  HomeIcon,
  HomeworkIcon,
  MediathekIcon,
  MeetingDefaultIcon,
  ProfileIcon,
  QuestionmarkCircleIcon,
  SettingsIcon,
  TemplatesIcon,
  Sidebar as UISidebar,
} from '@bp/ui-components';
import classNames from 'classnames';
import { memo, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useWindowDimensions } from 'utils/dimensions';
import { AppLayoutContext } from '../../context/AppLayoutContext';
import { useAuthClaims } from '../../hooks/useAuthClaims';
import { usePermissionChecker } from '../../hooks/usePermissionChecker';
import styles from './Sidebar.module.scss';

export const Sidebar = memo(() => {
  const { t } = useTranslation();
  const { pimAuthClaims } = useAuthClaims();
  const organization = pimAuthClaims.getOrganization();
  const location = useLocation();

  const { width } = useWindowDimensions();
  const showAbout = width < 770;

  const { sidebarIsOpen, setSidebarIsOpen, sidebarIsFixed, setSidebarIsFixed } = useContext(AppLayoutContext);

  const perms = usePermissionChecker();

  const classes = classNames(
    styles.sidebar,
    {
      [styles.open]: sidebarIsOpen,
    },
    'has-scrollbar',
    'visible-on-hover',
  );

  return (
    <UISidebar
      isDesktop={true}
      currentLocationPath={location.pathname}
      logoType={'bildungsplattform'}
      sidebarContent={[
        {
          title: t('common.forMe'),
          items: [
            {
              icon: <DashboardIcon />,
              link: '',
              title: t(`dashboard.title`),
            },
            {
              icon: <CoursesIcon />,
              link: 'courses',
              title: t(`courses.title`),
            },
            {
              icon: <GroupsIcon />,
              link: 'groups',
              title: t(`groups.title`),
            },
            ...(perms?.canViewClassbook(organization)
              ? [
                  {
                    icon: <ClassbookIcon />,
                    link: 'classbook',
                    title: t(`classbook.title`, { count: 1 }),
                  },
                ]
              : []),

            ...(perms?.canViewHomeworks(organization)
              ? [
                  {
                    icon: <HomeworkIcon />,
                    link: 'homeworks',
                    title: t('homeworks.title', { count: 2 }),
                  },
                ]
              : []),
            ...(perms?.canViewAbsences()
              ? [
                  {
                    icon: <AbsenceIcon />,
                    link: perms?.canViewOwnAbsencesOnly()
                      ? 'my-absences'
                      : perms.canViewOwnAbsences()
                        ? 'absences/my-absences'
                        : 'absences/administration',
                    title: t(`absences.title`, { count: 2 }),
                  },
                ]
              : []),
            ...(perms?.canViewTemplates()
              ? [
                  {
                    icon: <TemplatesIcon />,
                    link: 'templates',
                    title: t(`templates.title`),
                  },
                ]
              : []),
          ],
        },
        ...(perms?.canViewMediaLibrary()
          ? [
              {
                title: 'Community',
                items: [
                  {
                    icon: <MediathekIcon />,
                    link: 'mediaLibrary',
                    title: t(`mediaLibrary.title`),
                  },
                ],
              },
            ]
          : []),

        {
          title: t('institution.general'),
          items: [
            {
              icon: <HomeIcon />,
              link: 'institution',
              title: t('institution.title'),
            },
            ...(perms?.canViewMeetings(organization)
              ? [
                  {
                    icon: <MeetingDefaultIcon />,
                    link: 'meetings',
                    title: t('meetings.titlesShort'),
                  },
                ]
              : []),
            ...(perms?.canViewSettings(organization)
              ? [
                  {
                    icon: <SettingsIcon />,
                    link: 'settings',
                    title: t('settings.title'),
                  },
                ]
              : []),
          ],
        },
      ]}
      sidebarFooterItems={[
        {
          icon: <ProfileIcon />,
          link: 'profile',
          title: t('profiles.titleSingular'),
        },
        ...(perms?.canViewAdmin()
          ? [
              {
                icon: <SettingsIcon />,
                link: 'admin',
                title: 'Administration',
              },
            ]
          : []),

        ...(showAbout
          ? [
              {
                icon: <QuestionmarkCircleIcon />,
                link: 'app-about',
                title: t('common.about'),
              },
            ]
          : []),
      ]}
      isExpanded={sidebarIsOpen}
      setIsExpanded={setSidebarIsOpen}
      isFixed={sidebarIsFixed}
      setIsFixed={(value) => {
        setSidebarIsFixed(value);
      }}
      className={classes}
    />
  );
});

Sidebar.displayName = 'Sidebar';
